import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudSync, InfoOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService, ErpProductResyncResponse, ErpSyncAuditResponse } from '../services/api';
import { NeutralNote } from './productDetails/NeutralNote';
import { formatErpSyncCatalogLine } from '../utils/erpSyncAuditDisplay';
import { pickMappedLiveListing } from '../utils/erpSyncLiveListing';
import { tokens } from '../theme/tokens';
import { ErpSyncFieldChanges } from './ErpSyncFieldChanges';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { CopyProvenanceRow } from './productDetails/CopyProvenanceRow';
import { ResetShopifyLinkButton } from './productDetails/ResetShopifyLinkButton';

/** Audit rows a Reset Shopify link writes: a mapping change, not a push, so never the push outcome. */
const SHOPIFY_LINK_RESET_CODE = 'shopify_link_reset';

const money = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const when = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleString();
};

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'created' || status === 'updated') return 'success';
  if (status === 'skipped') return 'warning';
  if (status === 'error') return 'error';
  return 'default';
};

const platformLabel = (platform?: string | null): string => {
  if (platform === 'bigcommerce') return 'BigCommerce';
  if (platform === 'lightspeed') return 'Lightspeed';
  if (platform === 'lightspeed_ecom') return 'Lightspeed eCom';
  if (platform === 'shopify') return 'Shopify';
  return 'store';
};

const eventTimeMs = (event: { timestamp: string }): number => {
  const ms = new Date(event.timestamp).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const moneyEqual = (a: number | null | undefined, b: number | null | undefined): boolean => {
  // Both absent counts as a match: a listing with no compare-at price that was
  // pushed without one hasn't drifted, it just has nothing to compare.
  if (a == null && b == null) return true;
  return a != null && b != null && Math.abs(Number(a) - Number(b)) < 0.01;
};

/** "price", "price and SKU", "price, compare-at and SKU" */
const formatFieldList = (fields: string[]): string => {
  const lower = fields.map((f) => (f === 'SKU' ? f : f.toLowerCase()));
  if (lower.length <= 1) return lower.join('');
  return `${lower.slice(0, -1).join(', ')} and ${lower[lower.length - 1]}`;
};

interface SyncStatusLine {
  dot: string;
  title: string;
  detail: string;
}

/**
 * "Mapped to X" / "Not mapped" status line — mapped state comes from real
 * store mappings (data.mappings), most-recent push outcome from the latest
 * audit event (sorted defensively; the API's own ordering isn't guaranteed
 * by this component's contract).
 *
 * `drift` carries the same live-vs-last-pushed comparison the call-out rows
 * below render, so the headline never claims the listing is up to date while
 * a row underneath shows a mismatch.
 */
function resolveSyncStatusLine(
  data: ErpSyncAuditResponse | undefined,
  mapped: boolean,
  platform: string | undefined,
  drift: { comparable: boolean; mismatched: string[] },
  integrationId?: string,
  variant?: { shared: boolean; wrongVariant: boolean; pushedSku: string | null },
): SyncStatusLine {
  const label = platformLabel(platform);
  if (!mapped) {
    return {
      dot: tokens.ink.disabled,
      title: 'Not mapped to a store listing',
      detail: 'Nothing has been pushed. Add to sync to create the mapping on the next run.',
    };
  }
  const events = (integrationId
    ? (data?.events ?? []).filter((event) => event.integrationId === integrationId)
    : (data?.events ?? [])
  ).filter((event) => event.skipCode !== SHOPIFY_LINK_RESET_CODE);
  const latest = [...events].sort((a, b) => eventTimeMs(b) - eventTimeMs(a))[0];
  if (!latest) {
    return {
      dot: tokens.ink.disabled,
      title: `Mapped to your ${label} listing`,
      detail: 'Mapped, but nothing has been pushed yet.',
    };
  }
  if (latest.status === 'created' || latest.status === 'updated') {
    const mappedTitle = variant?.shared
      ? `Mapped to a variant on your ${label} listing`
      : `Mapped to your ${label} listing`;
    if (variant?.wrongVariant) {
      return {
        dot: tokens.signal.caution.solid,
        title: mappedTitle,
        detail: variant.pushedSku
          ? `This product is SKU ${variant.pushedSku}. The live price on the listing belongs to a different variant, so it is not compared here.`
          : 'The live price on the listing belongs to a different variant, so it is not compared here.',
      };
    }
    if (drift.mismatched.length > 0) {
      return {
        dot: tokens.signal.caution.solid,
        title: mappedTitle,
        detail: variant?.shared
          ? `Last push succeeded, but this variant differs on ${formatFieldList(drift.mismatched)}.`
          : `Last push succeeded, but the live listing differs on ${formatFieldList(drift.mismatched)}.`,
      };
    }
    return {
      dot: tokens.signal.positive.solid,
      title: mappedTitle,
      detail: variant?.shared
        ? 'This variant matches the last push.'
        : drift.comparable ? 'Live listing matches the last push.' : 'The last push succeeded.',
    };
  }
  if (latest.status === 'skipped') {
    return {
      dot: tokens.signal.caution.solid,
      title: `Mapped to your ${label} listing`,
      detail: 'The last push was skipped — the live listing still reflects the push before it.',
    };
  }
  return {
    dot: tokens.signal.problem.solid,
    title: `Mapped to your ${label} listing`,
    detail: 'The last push failed.',
  };
}

export function ErpSyncAuditPanel({
  productId,
  enabled = true,
}: {
  productId: string;
  /** Store users and admins impersonating a store. Admins without a store org get a disabled shell. */
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const { enabled: fieldHistoryOn } = useOrgFeatureFlag('sync_audit_field_history');
  const { enabled: copyProvenanceOn } = useOrgFeatureFlag('copy_provenance');
  const canUse = enabled && Boolean(productId);
  const { data, isLoading, error } = useQuery<ErpSyncAuditResponse>({
    queryKey: ['erp-sync-audit', productId],
    queryFn: () => apiService.getErpSyncAudit(productId),
    enabled: canUse,
    staleTime: 30_000,
  });

  const [resyncMessage, setResyncMessage] = useState<{ severity: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [forceDescription, setForceDescription] = useState(false);
  const [forceImages, setForceImages] = useState(false);

  useEffect(() => {
    setForceDescription(false);
    setForceImages(false);
    setResyncMessage(null);
  }, [productId]);

  const resyncMutation = useMutation({
    mutationFn: (opts?: { forcePricing?: boolean; integrationId?: string }) => apiService.forceErpResync(
      productId,
      opts?.integrationId || data?.mappings?.[0]?.integrationId || undefined,
      {
        forceDescription: opts?.forcePricing ? false : forceDescription,
        forceImages: opts?.forcePricing ? false : forceImages,
        forcePricing: opts?.forcePricing,
      }
    ),
    onSuccess: (result: ErpProductResyncResponse) => {
      queryClient.invalidateQueries({ queryKey: ['erp-sync-audit', productId] });
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      queryClient.invalidateQueries({ queryKey: ['sync-selection'] });
      const first = result.results[0];
      const label = platformLabel(first?.platform);
      const warning = first?.warnings?.[0]?.message;
      if (first?.status === 'success') {
        const extras = [
          forceDescription ? 'description' : null,
          forceImages ? 'images' : null,
        ].filter(Boolean);
        const base = extras.length
          ? `${label} listing was updated (${extras.join(' and ')}). Live price will refresh in a moment.`
          : `${label} listing was updated. Live price will refresh in a moment.`;
        setResyncMessage({
          severity: warning ? 'warning' : 'success',
          text: warning ? `${base} ${warning}` : base,
        });
      } else if (first?.status === 'skipped') {
        setResyncMessage({
          severity: 'warning',
          text: first.error || first.warnings?.[0]?.message || `${label} skipped this product.`,
        });
      } else {
        setResyncMessage({
          severity: 'error',
          text: first?.error || `${label} resync failed.`,
        });
      }
    },
    onError: (err: Error) => {
      setResyncMessage({ severity: 'error', text: err.message || 'Store resync failed.' });
    },
  });

  // Pair the live listing with the mapping it actually belongs to (shared with
  // the product hero's live storefront price).
  const mappings = data?.mappings ?? [];
  const liveEntries = data?.live ?? [];
  const { mapping, live } = pickMappedLiveListing(data);
  const lastPushed = mapping?.lastPushed;
  const mapped = mappings.length > 0;
  // Reset Shopify link acts on one Shopify integration, so a store with several shops gets one
  // control per shop, each naming its shop (mapping order from Firestore is not meaningful).
  const shopifyResetTargets = Array.from(
    new Map(
      mappings
        .filter((m) => m.platform === 'shopify' && m.integrationId)
        .map((m) => [
          m.integrationId,
          {
            integrationId: m.integrationId,
            shopDomain:
              m.shopDomain ?? liveEntries.find((l) => l.integrationId === m.integrationId)?.shopDomain ?? null,
            // No run could match the product again: the API refuses these, so the control says why.
            blockedReason:
              m.syncProducts === false
                ? ('product_sync_off' as const)
                : m.inSyncSelection === false
                  ? ('not_selected' as const)
                  : null,
          },
        ]),
    ).values(),
  ).sort((a, b) => (a.shopDomain || a.integrationId).localeCompare(b.shopDomain || b.integrationId));

  // Drift call-outs: what's actually live vs. what Motzu last pushed —
  // the two facts this panel can compare without extra props (no live
  // "current catalog price" is available here, only the push history).
  const sharedListing = (live?.variantCount ?? 0) > 1;
  const wrongVariant = live?.skuMatched === false;
  const driftRows = live && lastPushed && !wrongVariant
    ? [
        {
          label: 'Price',
          live: money(live.price),
          matches: moneyEqual(live.price, lastPushed.price),
          note: `last pushed ${money(lastPushed.price)}`,
        },
        {
          label: 'Compare-at',
          live: money(live.compareAtPrice),
          matches: moneyEqual(live.compareAtPrice, lastPushed.compareAtPrice),
          note: `last pushed ${money(lastPushed.compareAtPrice)}`,
        },
        {
          label: 'SKU',
          live: live.sku || '—',
          matches: (!live.sku && !lastPushed.sku) || (!!live.sku && !!lastPushed.sku && live.sku === lastPushed.sku),
          note: `last pushed ${lastPushed.sku || '—'}`,
        },
      ]
    : [];

  const linkOnly = mapping?.platform === 'shopify' && mapping.syncMode === 'link_only';
  const pricingOff = linkOnly && mapping?.syncPricing === false;
  const statusLine = resolveSyncStatusLine(
    data,
    mapped,
    mapping?.platform ?? (live ? 'shopify' : undefined),
    { comparable: driftRows.length > 0, mismatched: driftRows.filter((row) => !row.matches).map((row) => row.label) },
    mapping?.integrationId,
    live
      ? {
          shared: sharedListing,
          wrongVariant,
          pushedSku: lastPushed?.sku || null,
        }
      : undefined,
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        minWidth: 0,
        overflow: 'hidden',
        ...(!canUse ? { bgcolor: tokens.surface.sunken } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap', ...(!canUse ? { opacity: 0.5 } : {}) }}>
        <CloudSync sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', flex: 1, minWidth: 0 }}>
          Store listing sync
        </Typography>
        <Tooltip
          title="Pushes this product to the store now, even if nothing changed since the last sync. It follows your sync mode, except that ticking Update description or Replace images overrides the mode's protection for the title and description, or the photos, on this push (status, vendor, tags and category are still untouched). Those options apply to this one push only and are not saved, so tick them and click Force resync right away."
          arrow
        >
          <InfoOutlined aria-label="What Force resync does" sx={{ fontSize: 16, color: 'text.secondary' }} />
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={resyncMutation.isPending ? <CircularProgress size={14} /> : <CloudSync />}
          onClick={() => {
            setResyncMessage(null);
            resyncMutation.mutate({});
          }}
          disabled={!canUse || resyncMutation.isPending}
          sx={{ textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
        >
          Force resync
        </Button>
      </Box>

      {!canUse && (
        <Box sx={{ mb: 1.5 }}>
          <NeutralNote>
            Select a store organization via impersonation to view and manage listing sync from admin.
          </NeutralNote>
        </Box>
      )}

      {canUse && !isLoading && !error && linkOnly && (
        <Alert severity={pricingOff ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
          Link only leaves titles, photos, and listing copy as you set them.
          {pricingOff
            ? ' Price syncing is off, so scheduled sync will not change this price.'
            : ' Sync pricing is on, so the matching variant’s price updates on each sync.'}
          {pricingOff && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setResyncMessage(null);
                  resyncMutation.mutate({
                    forcePricing: true,
                    integrationId: mapping?.integrationId,
                  });
                }}
                disabled={resyncMutation.isPending}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Force price
              </Button>
            </Box>
          )}
        </Alert>
      )}

      {canUse && !isLoading && !error && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            mb: 1.5,
            p: 1.25,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: tokens.surface.subtle,
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusLine.dot, mt: 0.6, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {statusLine.title}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.ink.muted }}>
              {statusLine.detail}
            </Typography>
          </Box>
          {shopifyResetTargets.length > 0 && (
            <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              {shopifyResetTargets.map((target) => (
                <ResetShopifyLinkButton
                  key={target.integrationId}
                  integrationId={target.integrationId}
                  productId={productId}
                  shopDomain={target.shopDomain}
                  showShopInLabel={shopifyResetTargets.length > 1}
                  blockedReason={target.blockedReason}
                  disabled={resyncMutation.isPending}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1, ...(!canUse ? { opacity: 0.5 } : {}) }}>
        <FormControlLabel
          disabled={!canUse}
          control={
            <Checkbox
              size="small"
              checked={forceDescription}
              onChange={(e) => setForceDescription(e.target.checked)}
              disabled={!canUse || resyncMutation.isPending}
            />
          }
          label={<Typography variant="caption">Update description</Typography>}
          sx={{ mr: 0.5 }}
        />
        <FormControlLabel
          disabled={!canUse}
          control={
            <Checkbox
              size="small"
              checked={forceImages}
              onChange={(e) => setForceImages(e.target.checked)}
              disabled={!canUse || resyncMutation.isPending}
            />
          }
          label={<Typography variant="caption">Replace images</Typography>}
        />
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1, lineHeight: 1.45, ...(!canUse ? { opacity: 0.5 } : {}) }}
      >
        Update description rewrites the title and description. Replace images swaps the photos (new ones upload first). Neither changes status, vendor, tags or category,
        and neither is saved: they apply to the next Force resync only, so tick them and click Force resync right away (the page Save button does not keep them).
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: { xs: 'none', md: 'block' }, mb: 1.5, lineHeight: 1.45, ...(!canUse ? { opacity: 0.5 } : {}) }}
      >
        Prices Motzu actually pushed to this store&apos;s commerce listing (Shopify, BigCommerce, or Lightspeed), plus the live Shopify price when that mapping exists.
        History starts after this audit shipped — older $30-style pushes only show if they are still live on the storefront.
        Force resync pushes this product now and ignores the &quot;already up to date&quot; skip. MAP − instant rebate sales go out as sale price with MAP as the compare-at / list price.
        Check <strong>Update description</strong> to overwrite listing copy (title, description, specs) even if the store is in enrich or link-only mode.
        Check <strong>Replace images</strong> to delete the store&apos;s current photos and upload Motzu&apos;s gallery — needed when the listing was created with only a couple of shots.
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: { xs: 'block', md: 'none' }, mb: 1.5, lineHeight: 1.45, ...(!canUse ? { opacity: 0.5 } : {}) }}
      >
        Prices Motzu pushed to this store listing, plus the live Shopify price when that mapping exists.
      </Typography>
      {resyncMessage && (
        <Alert severity={resyncMessage.severity} sx={{ mb: 1.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
          {resyncMessage.text}
        </Alert>
      )}

      {canUse && isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">Loading sync history…</Typography>
        </Box>
      )}

      {canUse && error && (
        <Alert severity="warning" sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
          {(error as Error).message || 'Could not load store sync history.'}
        </Alert>
      )}

      {canUse && !isLoading && !error && (
        <Stack spacing={1.5}>
          {live && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: tokens.ink.faint }}>
                  {sharedListing ? 'This variant on' : 'Live on'} {platformLabel(mapping?.platform ?? 'shopify')}
                </Typography>
                {live.adminUrl && (
                  <Link href={live.adminUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.75rem' }}>
                    Open listing
                  </Link>
                )}
                <Typography variant="caption" sx={{ color: tokens.ink.faint, width: { xs: '100%', sm: 'auto' }, ml: { sm: 'auto' } }}>
                  Listing updated {when(live.updatedAt)}
                </Typography>
              </Box>
              {sharedListing && !wrongVariant && (
                <Typography variant="caption" sx={{ color: tokens.ink.muted }}>
                  {[live.variantTitle, live.sku ? `SKU ${live.sku}` : null, `${live.variantCount} variants on this listing`]
                    .filter(Boolean)
                    .join(' · ')}
                  . Only this variant is compared with the last push.
                </Typography>
              )}
              {live.error && (
                <Typography variant="caption" color="error.main">{live.error}</Typography>
              )}
              {!wrongVariant && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.max(driftRows.length, 1)}, minmax(0, 1fr))` }, gap: 1 }}>
                {(driftRows.length > 0
                  ? driftRows
                  : [{
                      label: 'Price',
                      live: money(live.price),
                      // Wrong variant: comparison was skipped — do not claim a match.
                      matches: !wrongVariant,
                      note: wrongVariant ? 'different variant — not compared' : '',
                    }]).map((row) => (
                  <Box
                    key={row.label}
                    sx={{
                      p: '9px 11px',
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: row.matches ? tokens.line.hairline : 'divider',
                      bgcolor: row.matches ? 'background.paper' : tokens.surface.sunken,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: tokens.ink.faint, display: 'block' }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{row.live}</Typography>
                    <Typography variant="caption" sx={{ color: row.matches ? tokens.ink.faint : tokens.signal.caution.ink }}>
                      {row.matches ? 'matches last push' : row.note}
                    </Typography>
                  </Box>
                ))}
              </Box>
              )}
            </Box>
          )}

          {lastPushed && (
            <Typography variant="caption" sx={{ color: tokens.ink.muted }}>
              Last Motzu push: {money(lastPushed.price)}
              {lastPushed.compareAtPrice ? ` (was ${money(lastPushed.compareAtPrice)})` : ''}
              {lastPushed.at ? ` · ${when(lastPushed.at)}` : ''}
            </Typography>
          )}

          {copyProvenanceOn && lastPushed?.copyProvenance && (
            <CopyProvenanceRow provenance={lastPushed.copyProvenance} />
          )}

          {(data?.events?.length ?? 0) > 0 ? (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'grid' },
                  gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'minmax(140px, 0.9fr) 88px minmax(160px, 1.2fr) 1fr' },
                  px: 1.25,
                  py: 0.75,
                  bgcolor: tokens.surface.subtle,
                }}
              >
                {['When', 'Result', 'Pushed to store', 'Catalog at sync'].map((label) => (
                  <Typography
                    key={label}
                    variant="caption"
                    sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, color: tokens.ink.faint }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>
              {data!.events.map((event, index) => {
                const catalogLine = formatErpSyncCatalogLine(event.catalog, event.pushed, money);
                const pushedLine = `${platformLabel(event.platform)} ${
                  event.pushed?.price != null ? money(event.pushed.price) : '—'
                }${event.pushed?.compareAtPrice != null ? ` / was ${money(event.pushed.compareAtPrice)}` : ''}`;
                return (
                  <Box
                    key={event.id}
                    sx={{
                      px: 1.25,
                      py: 0.85,
                      borderTop: index === 0 ? { xs: 'none', sm: '1px solid' } : '1px solid',
                      borderColor: tokens.line.hairline,
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: 'none', sm: 'grid' },
                        gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'minmax(140px, 0.9fr) 88px minmax(160px, 1.2fr) 1fr' },
                        alignItems: 'start',
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: tokens.ink.faint }}>{when(String(event.timestamp))}</Typography>
                      <Chip
                        label={event.status}
                        size="small"
                        color={statusColor(event.status)}
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, textTransform: 'capitalize', width: 'fit-content' }}
                      />
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.primary' }}>
                          {pushedLine}
                        </Typography>
                        {event.error && (
                          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.35, color: tokens.signal.caution.ink }}>
                            {event.error}
                          </Typography>
                        )}
                        {event.skipCode && (
                          <Chip
                            label={event.skipCode}
                            size="small"
                            sx={{
                              mt: 0.35,
                              height: 18,
                              fontSize: '0.6rem',
                              fontFamily: tokens.font.mono,
                              bgcolor: tokens.surface.tint,
                              color: 'text.secondary',
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ lineHeight: 1.4, color: tokens.ink.muted }}>
                        {catalogLine}
                      </Typography>
                    </Box>
                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: tokens.ink.faint }}>{when(String(event.timestamp))}</Typography>
                        <Chip
                          label={event.status}
                          size="small"
                          color={statusColor(event.status)}
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, textTransform: 'capitalize', width: 'fit-content' }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.primary' }}>
                        {pushedLine}
                      </Typography>
                      {event.error && (
                        <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.35, color: tokens.signal.caution.ink }}>
                          {event.error}
                        </Typography>
                      )}
                      {event.skipCode && (
                        <Chip
                          label={event.skipCode}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontFamily: tokens.font.mono,
                            bgcolor: tokens.surface.tint,
                            color: 'text.secondary',
                            width: 'fit-content',
                          }}
                        />
                      )}
                      <Typography variant="caption" sx={{ lineHeight: 1.4, color: tokens.ink.muted }}>
                        Catalog {catalogLine}
                      </Typography>
                    </Box>
                    {fieldHistoryOn && <ErpSyncFieldChanges changes={event.fieldChanges} />}
                  </Box>
                );
              })}
            </Box>
          ) : (
            !isLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                No Motzu push records yet for this product. The next sync will log price and product changes here.
              </Typography>
            )
          )}
        </Stack>
      )}
    </Paper>
  );
}
